/**
 * useFragmentRecombine.ts — PLAT-10270 (T10, epic PLAT-10209)
 *
 * The Fragment-view **Backspace recombine** gesture: with the caret at the
 * very start of a fragment in the workspace's translated pane, Backspace
 * merges that fragment into the previous fragment of the SAME segment via
 * `TranslationStore.mergeFragmentIntoPrevious` — a pure phrase-array
 * transform + verbatim Save. It never invokes the karaoke caption commands
 * (`MergeAdjacentCaptionClipsCommand` is a different surface — model only).
 *
 * Containment (epic D9): the pane isn't focusable, so keydown targets
 * `document.body` — a subtree listener would never fire. We therefore
 * register a WINDOW-level capture-phase listener (the `TranslatedCaptionsView`
 * break-editing precedent), but only while `enabled` (workspace mounted AND
 * Fragment view), and scope every event to the workspace's own pane via the
 * guard chain, evaluated in order:
 *
 *   1. Backspace only.
 *   2. Typing guard — never steal keys from a real input / textarea /
 *      contenteditable (bail WITHOUT consuming).
 *   3. Caret resolution (pure, tested): the selection must be a collapsed
 *      caret inside `translatedColumnRef` on a `[data-initial-index]` span —
 *      anything else bails WITHOUT consuming (the caret isn't ours; CLAUDE.md
 *      #12 local-container scoping, same resolution as `useSpanReplace`).
 *   4. Once in-pane: ALWAYS consume (`preventDefault` +
 *      `stopImmediatePropagation` — a silent no-op beats a stray page-level
 *      Backspace, the captions-view rule), then act only when the caret sits
 *      at the START of the fragment. The store guards first-fragment /
 *      cross-segment / solo internally (resolves false, no save) — no
 *      segment logic here.
 */

import { type RefObject, useEffect, useRef } from 'react';

import type { TranslationStore } from '@src/stores-v2/TranslationStore';

/** Where the caret sits, resolved against the translated pane's spans. */
export interface RecombineCaretResolution {
  /** The `data-initial-index` (source lead) of the fragment under the caret. */
  lead: number;
  /**
   * True when the caret sits at offset 0 of the fragment's FIRST
   * `[data-initial-index]` span for that lead (first in DOM order) — the only
   * position where Backspace merges. Any other in-pane position is false.
   */
  atStart: boolean;
}

/** The first text node inside `el`, in DOM order (the fragment's text). */
function firstTextNodeOf(el: HTMLElement): Node | null {
  const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  return walker.nextNode();
}

/**
 * Resolve the current selection to a fragment caret position, scoped to
 * `container` (the workspace's translated pane — CLAUDE.md #12). Pure over
 * its inputs so the fiddly caret shapes are unit-testable without key events.
 *
 * Returns null when the caret is not ours: no selection, a non-collapsed
 * range, a start node outside `container`, or no enclosing
 * `[data-initial-index]` element. Otherwise `lead` is the enclosing span's
 * stamped source lead, and `atStart` is true only for the two caret shapes
 * that mean "before the first character": the span's first text node at
 * `startOffset === 0`, or the span element itself at offset 0.
 */
export function resolveRecombineCaret(
  container: HTMLElement | null,
  selection: Selection | null,
): RecombineCaretResolution | null {
  if (!container || !selection || selection.rangeCount === 0) return null;
  if (!selection.isCollapsed) return null;
  const range = selection.getRangeAt(0);
  const startNode = range.startContainer;
  if (!container.contains(startNode)) return null;
  const startEl =
    startNode instanceof HTMLElement ? startNode : startNode.parentElement;
  const span = startEl?.closest<HTMLElement>('[data-initial-index]');
  if (!span || !container.contains(span)) return null;
  const lead = Number(span.getAttribute('data-initial-index'));
  if (!Number.isFinite(lead)) return null;

  // The fragment's FIRST span for this lead in DOM order — the
  // `container.querySelector` convention shared with `useSpanReplace`.
  const firstSpan = container.querySelector<HTMLElement>(
    `[data-initial-index='${lead}']`,
  );
  const atStart =
    span === firstSpan &&
    range.startOffset === 0 &&
    (startNode === span || startNode === firstTextNodeOf(span));
  return { lead, atStart };
}

export interface UseFragmentRecombineParams {
  /**
   * Opt-in (D9): true only while the workspace shows Fragment view. When
   * false no listener is registered — the gesture cannot react at all.
   */
  enabled: boolean;
  /** The translated language under review (BCP 47). */
  language: string;
  /** The translated (middle) column element — the caret is resolved here. */
  translatedColumnRef: RefObject<HTMLElement>;
  /** The store owning the merge transform (injected — keeps the hook testable). */
  translationStore: Pick<TranslationStore, 'mergeFragmentIntoPrevious'>;
}

/** PLAT-10270 — the review-scoped Backspace recombine gesture (see header). */
export function useFragmentRecombine({
  enabled,
  language,
  translatedColumnRef,
  translationStore,
}: UseFragmentRecombineParams): void {
  // Latest values for the keydown closure — refs keep the listener registered
  // once per `enabled` flip instead of re-binding per render (the
  // `TranslatedCaptionsView` refs-for-freshness precedent).
  const languageRef = useRef(language);
  languageRef.current = language;
  const storeRef = useRef(translationStore);
  storeRef.current = translationStore;

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace') return;
      // Don't steal Backspace while the user is typing in a real input.
      const ae = document.activeElement;
      if (
        ae &&
        (ae.tagName === 'INPUT' ||
          ae.tagName === 'TEXTAREA' ||
          (ae instanceof HTMLElement && ae.isContentEditable))
      ) {
        return;
      }
      const caret = resolveRecombineCaret(
        translatedColumnRef.current,
        window.getSelection(),
      );
      // Not our caret (outside the pane / no fragment span / no collapsed
      // selection) — let the event through untouched.
      if (!caret) return;
      // In-pane: always consume — a silent no-op is better than a stray
      // page-level Backspace behavior (the captions-view rule).
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!caret.atStart) return;
      // The store guards first-fragment / cross-segment / solo internally
      // (resolves false, no save) — fire-and-forget.
      void storeRef.current.mergeFragmentIntoPrevious(
        languageRef.current,
        caret.lead,
      );
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, translatedColumnRef]);
}
